import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import {
  Settings,
  Save,
  Upload,
  Trash2,
  User,
  Building,
  FileText,
  Clock,
  Palette,
  Phone,
  Mail,
  Globe,
  MapPin,
  CreditCard,
  IdCard,
  Receipt,
  Camera,
  XCircle,
  RefreshCw,
  CalendarDays,
  ListFilter,
  ChevronDown,
  ArrowRight,
  ShieldCheck,
} from 'lucide-react';
import api from '../services/api';
import GoogleCalendarIntegrationSection from '../components/integrations/GoogleCalendarIntegrationSection';
import { CUSTOMIZABLE_MENU_ITEMS } from '../config/menuItems';

const SETTINGS_TABS = [
  { id: 'basicos', name: 'Dados Básicos', icon: Building },
  { id: 'profissionais', name: 'Profissionais', icon: User },
  { id: 'especialidades', name: 'Especialidades', icon: Settings },
  { id: 'documentos', name: 'Documentos', icon: FileText },
  { id: 'horarios', name: 'Horários', icon: Clock },
  { id: 'integracoes', name: 'Integrações', icon: CalendarDays },
  { id: 'visual', name: 'Visual', icon: Palette },
  { id: 'menu', name: 'Menu', icon: ListFilter },
];

const INITIAL_SETTINGS_TABS = SETTINGS_TABS.filter((tab) =>
  ['basicos', 'profissionais', 'especialidades', 'documentos', 'horarios'].includes(tab.id)
);

const ADDITIONAL_SETTINGS_TABS = SETTINGS_TABS.filter((tab) =>
  ['integracoes', 'visual', 'menu'].includes(tab.id)
);

const ADDITIONAL_SETTINGS_TAB_IDS = new Set(
  ADDITIONAL_SETTINGS_TABS.map((tab) => tab.id)
);

const ESSENTIAL_MENU_HIDDEN_ITEMS = [
  'whatsapp_business',
  'aligners',
  'ai_analysis',
  'clinical_documents',
  'contracts',
  'inventory',
  'billing',
];

const SettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedComplianceContinuation = new URLSearchParams(location.search).get('continue') === 'compliance';
  const { showToast } = useToast();
  const {
    user,
    reloadAuthStatus,
    menuPreferences,
    updateMenuPreferences,
  } = useAuth();
  const [pendingComplianceAssessment, setPendingComplianceAssessment] = useState(false);
  const continueToCompliance = Boolean(
    requestedComplianceContinuation ||
    user?.pending_compliance_assessment_id ||
    pendingComplianceAssessment
  );
  const [hiddenMenuItems, setHiddenMenuItems] = useState([]);
  const [menuPreset, setMenuPreset] = useState('complete');

  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = new URLSearchParams(window.location.search).get('tab');
    return SETTINGS_TABS.some((tab) => tab.id === requestedTab)
      ? requestedTab
      : 'basicos';
  });
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(() =>
    ADDITIONAL_SETTINGS_TAB_IDS.has(activeTab)
  );
  const [billingInfo, setBillingInfo] = useState({
    cpf_cnpj: '',
    postal_code: '',
    address: '',
    address_number: '',
    complement: '',
    province: '',
    city: '',
    state: '',
  });

  useEffect(() => {
    if (user?.billing_info) {
      setBillingInfo(user.billing_info);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab !== 'basicos') return;

    let cancelled = false;
    const detectPendingAssessment = async () => {
      try {
        const response = await api.get('/compliance');
        if (!cancelled) {
          setPendingComplianceAssessment(Boolean(response.data?.pending_assessment));
        }
      } catch (error) {
        // A consulta é apenas contextual. Contas sem acesso ao módulo mantêm
        // silenciosamente o fluxo normal de Configurações.
        if (!cancelled) setPendingComplianceAssessment(false);
      }
    };

    detectPendingAssessment();
    return () => { cancelled = true; };
  }, [activeTab]);

  const handleBillingChange = (field, value) => {
    setBillingInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleCepBlurBilling = async () => {
    const cep = billingInfo.postal_code?.replace(/\D/g, '');
    if (cep?.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setBillingInfo((prev) => ({
          ...prev,
          address: data.logradouro || '',
          province: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
        }));
      }
    } catch (e) {
      console.error(e);
    }
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingProfessionalPhoto, setUploadingProfessionalPhoto] = useState({});
  const [uploadingCert, setUploadingCert] = useState(false);
  const [nfseCertPassword, setNfseCertPassword] = useState('');
  const [availableSpecialties, setAvailableSpecialties] = useState([]);

  const [savedOnlineBookingToken, setSavedOnlineBookingToken] = useState('');
  const [onlineTokenStatus, setOnlineTokenStatus] = useState({
    checking: false,
    available: null,
    message: '',
  });
  const [copyLinkMessage, setCopyLinkMessage] = useState('');

  const [whatsappModuleEvents, setWhatsappModuleEvents] = useState([]);
  const [whatsappModuleProcessing, setWhatsappModuleProcessing] = useState(false);
  const [whatsappModulePaymentMethod, setWhatsappModulePaymentMethod] = useState('credit_card');

  const [settings, setSettings] = useState({
    clinic_name: '',
    clinic_legal_name: '',
    professional_name: '',
    address: '',
    address_street: '',
    address_number: '',
    address_complement: '',
    address_neighborhood: '',
    address_city: '',
    address_state: '',
    address_cep: '',
    phone: '',

    clinic_phone: '',
    clinic_whatsapp: '',

    email: '',
    website: '',
    cnpj: '',
    cpf: '',
    cro_number: '',
    cro_state: '',
    logo_data: '',
    logo_filename: '',
    specialties: [],
    // NOVO: lista de profissionais da clínica
    professionals: [],
    working_hours: {
      monday: { start: '08:00', end: '18:00', enabled: true, break_enabled: false, break_start: '12:00', break_end: '14:00' },
      tuesday: { start: '08:00', end: '18:00', enabled: true, break_enabled: false, break_start: '12:00', break_end: '14:00' },
      wednesday: { start: '08:00', end: '18:00', enabled: true, break_enabled: false, break_start: '12:00', break_end: '14:00' },
      thursday: { start: '08:00', end: '18:00', enabled: true, break_enabled: false, break_start: '12:00', break_end: '14:00' },
      friday: { start: '08:00', end: '18:00', enabled: true, break_enabled: false, break_start: '12:00', break_end: '14:00' },
      saturday: { start: '08:00', end: '12:00', enabled: false, break_enabled: false, break_start: '12:00', break_end: '14:00' },
      sunday: { start: '08:00', end: '12:00', enabled: false, break_enabled: false, break_start: '12:00', break_end: '14:00' },
    },
    appointment_duration: 30,

    appointment_email_enabled: true,
    appointment_whatsapp_enabled: false,
    appointment_reminder_enabled: false,
    appointment_whatsapp_reminder_enabled: false,
    appointment_reminder_days_before: 1,

    appointment_whatsapp_auto_confirmation: false,
    appointment_whatsapp_auto_reschedule: false,
    appointment_whatsapp_auto_cancel: false,    

    online_booking_enabled: false,
    online_booking_token: '',
    online_booking_days_ahead: 30,
    online_booking_min_notice_hours: 2,
    online_booking_notify_clinic: true,
    online_booking_requires_manual_confirmation: false,
    online_booking_allow_patient_cancel: true,
    online_booking_allow_patient_reschedule: true,
    online_booking_cancel_min_notice_hours: 2,
    online_booking_reschedule_min_notice_hours: 2,
    primary_color: '#7c3aed',
    secondary_color: '#f3f4f6',
    // Configurações fiscais / NFS-e
    nfse_enabled: false,
    nfse_mode: 'simulado',
    nfse_environment: 'homologacao',
    nfse_municipio_codigo: '',
    nfse_inscricao_municipal: '',
    nfse_codigo_servico: '',
    nfse_aliquota_iss: null,
    nfse_optante_simples: false,
    nfse_cert_uploaded: false,
    nfse_cert_filename: '',
    nfse_cert_storage_path: '',
    nfse_cert_password_set: false,
  });

  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (requestedTab && SETTINGS_TABS.some((tab) => tab.id === requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);

  useEffect(() => {
    if (ADDITIONAL_SETTINGS_TAB_IDS.has(activeTab)) {
      setShowAdditionalSettings(true);
    }
  }, [activeTab]);

  useEffect(() => {
    const savedHiddenItems = menuPreferences?.hidden_items || [];
    setHiddenMenuItems(savedHiddenItems);
    setMenuPreset(
      menuPreferences?.preset || (savedHiddenItems.length === 0 ? 'complete' : 'custom')
    );
  }, [menuPreferences]);

  useEffect(() => {
    if (settings.whatsapp_module_payment_method) {
      setWhatsappModulePaymentMethod(settings.whatsapp_module_payment_method);
    }
  }, [settings.whatsapp_module_payment_method]);

  const handleMenuItemToggle = (itemId) => {
    setMenuPreset('custom');
    setHiddenMenuItems((current) =>
      current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId]
    );
  };

  const applyMenuPreset = (preset) => {
    setMenuPreset(preset);
    setHiddenMenuItems(preset === 'essential' ? ESSENTIAL_MENU_HIDDEN_ITEMS : []);
  };

  const dayNames = {
    monday: 'Segunda-feira',
    tuesday: 'Terça-feira',
    wednesday: 'Quarta-feira',
    thursday: 'Quinta-feira',
    friday: 'Sexta-feira',
    saturday: 'Sábado',
    sunday: 'Domingo',
  };

  const safeToast = {
    success: (msg) => showToast?.(msg, 'success'),
    error: (msg) => showToast?.(msg, 'error'),
    info: (msg) => showToast?.(msg, 'info'),
  };

  const safeErrorMessage = (error, fallbackMessage) => {
    if (!error) return fallbackMessage;
    const responseData = error.response?.data;
    if (typeof responseData === 'string') return responseData;
    if (error.response?.status === 403 && responseData?.detail) {
      return responseData.detail;
    }
    if (responseData?.detail) {
      if (typeof responseData.detail === 'string') return responseData.detail;
      if (Array.isArray(responseData.detail)) {
        const msg = responseData.detail[0]?.msg || responseData.detail[0];
        if (msg) return msg;
      }
    }
    return fallbackMessage;
  };

  const isMobileOrTablet =
    typeof window !== 'undefined' &&
    /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(window.navigator.userAgent);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/clinic-settings');
      const data = response.data || {};

      setSavedOnlineBookingToken(data.online_booking_token || '');

      // Monta lista de profissionais a partir dos dados retornados
      let professionals = Array.isArray(data.professionals)
        ? data.professionals
        : [];

      // Se ainda não existir lista, cria um profissional principal a partir dos campos antigos
      if (
        professionals.length === 0 &&
        (data.professional_name || data.cro_number || data.cro_state)
      ) {
        professionals = [
          {
            id: 'main',
            name: data.professional_name || '',
            cro_number: data.cro_number || '',
            cro_state: data.cro_state || '',
            specialties: [],
            email: data.email || '',
            phone: data.phone || '',
            photo_url: '',
            photo_filename: '',
            is_active: true,
            is_main: true,
          },
        ];
      }

      setSettings((prev) => ({
        ...prev,
        ...data,
        professionals,
      }));
    } catch (error) {
      console.error('Error fetching settings:', error);
      const msg = safeErrorMessage(
        error,
        'Erro ao carregar configurações da clínica',
      );
      safeToast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchWhatsappModuleEvents = async () => {
    try {
      const response = await api.get('/clinic-settings/whatsapp-module/events');
      setWhatsappModuleEvents(response.data?.events || []);
    } catch (error) {
      console.error('Erro ao carregar histórico do módulo WhatsApp:', error);
    }
  };

  const activateWhatsappModulePayment = async () => {
    if (whatsappModuleProcessing) return;
    setWhatsappModuleProcessing(true);
    try {
      if (['trial', 'courtesy', 'academic', 'pending_payment'].includes(user?.subscription_status)) {
        const msg = 'Para contratar o Módulo WhatsApp Business, é necessário ter uma assinatura paga ativa. Se você está em trial, cortesia ou benefício acadêmico, ative sua assinatura principal primeiro.';
        safeToast.error(msg);
        window.alert(msg);
        return;
      }

      if (settings?.whatsapp_module_status === 'cancel_pending') {
        const msg = 'O módulo já está com cancelamento solicitado.';
        safeToast.error(msg);
        window.alert(msg);
        return;
      }
      const cpfCnpj =
        user?.billing_info?.cpf_cnpj ||
        user?.cpf_cnpj ||
        settings.cnpj ||
        settings.cpf ||
        '';

      const billingPhone =
        user?.billing_info?.billing_phone ||
        user?.billing_phone ||
        user?.phone ||
        settings.phone ||
        '';

      if (!cpfCnpj) {
        safeToast.error('Informe o CPF ou CNPJ do pagador na página Assinatura antes de contratar o módulo.');
        return;
      }

      if (!billingPhone || onlyDigits(billingPhone).length !== 11) {
        safeToast.error('Informe um celular válido do pagador na página Assinatura antes de contratar o módulo.');
        return;
      }

      const response = await api.post('/subscriptions/whatsapp-module/activate', {
        payment_method: whatsappModulePaymentMethod,
        cpf_cnpj: cpfCnpj,
        billing_phone: onlyDigits(billingPhone),
      });

      await fetchSettings();
      await fetchWhatsappModuleEvents();

      if (response.data?.invoice_url) {
        window.open(response.data.invoice_url, '_blank', 'noopener,noreferrer');
        safeToast.success(
          response.data?.reused
            ? 'Cobrança existente recuperada. Conclua o pagamento no Asaas.'
            : 'Cobrança do módulo criada. Configure o pagamento no Asaas.'
        );
      } else {
        safeToast.success('Cobrança do módulo criada com sucesso.');
      }
    } catch (error) {
      const msg = safeErrorMessage(error, 'Erro ao contratar módulo WhatsApp.');
      safeToast.error(msg);
      window.alert(msg);
    } finally {
      setWhatsappModuleProcessing(false);
    }
  };

  const fetchSpecialties = async () => {
    try {
      const response = await api.get('/clinic-settings/specialties');
      setAvailableSpecialties(response.data?.specialties || []);
    } catch (error) {
      console.error('Error fetching specialties:', error);
    }
  };


  // Monta endereço em formato único (compatibilidade com campos legados e PDFs),
  // a partir dos campos estruturados.
  const composeBasicAddress = (next) => {
    const parts = [];
    const street = (next.address_street || '').trim();
    const number = (next.address_number || '').trim();
    const complement = (next.address_complement || '').trim();
    const neighborhood = (next.address_neighborhood || '').trim();
    const city = (next.address_city || '').trim();
    const state = (next.address_state || '').trim();
    const cep = (next.address_cep || '').trim();

    const line1 = [street, number].filter(Boolean).join(', ');
    const line2 = [complement, neighborhood].filter(Boolean).join(' - ');
    const line3 = [city, state].filter(Boolean).join(' - ');
    const line4 = cep ? `CEP ${cep}` : '';

    [line1, line2, line3, line4].forEach((l) => {
      if (l && l.trim()) parts.push(l.trim());
    });

    return parts.join(' / ');
  };

  const handleInputChange = (field, value) => {
    setSettings((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      // Se o usuário editar os campos estruturados do endereço,
      // atualiza também o campo "address" (texto único) para compatibilidade.
      const addressFields = new Set([
        'address_street',
        'address_number',
        'address_complement',
        'address_neighborhood',
        'address_city',
        'address_state',
        'address_cep',
      ]);

      if (addressFields.has(field)) {
        next.address = composeBasicAddress(next);
      }

      return next;
    });
  };

  const handleWorkingHoursChange = (day, field, value) => {
    setSettings((prev) => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: {
          ...prev.working_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleToggleWorkingDay = (day) => {
    setSettings((prev) => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: {
          ...prev.working_hours[day],
          enabled: !prev.working_hours[day].enabled,
        },
      },
    }));
  };

  const handleSpecialtyToggle = (specialty) => {
    setSettings((prev) => {
      const current = prev.specialties || [];
      const exists = current.includes(specialty);
      return {
        ...prev,
        specialties: exists
          ? current.filter((s) => s !== specialty)
          : [...current, specialty],
      };
    });
  };

  const handleProfessionalFieldChange = (index, field, value) => {
    setSettings((prev) => ({
      ...prev,
      professionals: prev.professionals.map((prof, i) =>
        i === index
          ? {
              ...prof,
              [field]: value,
            }
          : prof,
      ),
    }));
  };

  const handleAddProfessional = () => {
    setSettings((prev) => ({
      ...prev,
      professionals: [
        ...prev.professionals,
        {
          id: `prof_${Date.now()}`,
          name: '',
          cro_number: '',
          cro_state: '',
          specialty: '',
          primary_specialty: '',
          specialties: [],
          email: '',
          phone: '',
          photo_url: '',
          photo_filename: '',
          is_active: true,
          is_main: prev.professionals.length === 0,
        },
      ],
    }));
  };

  const handleRemoveProfessional = (index) => {
    setSettings((prev) => {
      const updated = prev.professionals.filter((_, i) => i !== index);
      // Garante que ainda exista um principal
      if (!updated.some((p) => p.is_main) && updated.length > 0) {
        updated[0].is_main = true;
      }
      return {
        ...prev,
        professionals: updated,
      };
    });
  };

  const handleSetMainProfessional = (index) => {
    setSettings((prev) => ({
      ...prev,
      professionals: prev.professionals.map((prof, i) => ({
        ...prof,
        is_main: i === index,
      })),
    }));
  };

  const handleProfessionalPhotoUpload = async (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const professional = settings.professionals?.[index];

    if (!professional?.id) {
      safeToast.error('Salve o profissional antes de enviar a foto.');
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      safeToast.error('Envie uma imagem JPG, PNG ou WEBP.');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      safeToast.error('A foto deve ter no máximo 8MB.');
      return;
    }

    try {
      setUploadingProfessionalPhoto((prev) => ({
        ...prev,
        [professional.id]: true,
      }));

      const formData = new FormData();
      formData.append('photo', file);

      await api.post(`/clinic-settings/professionals/${professional.id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      safeToast.success('Foto do profissional atualizada com sucesso!');
      await fetchSettings();
    } catch (error) {
      console.error('❌ Erro ao enviar foto do profissional:', error);
      const msg = safeErrorMessage(error, 'Erro ao enviar foto do profissional');
      safeToast.error(msg);
    } finally {
      setUploadingProfessionalPhoto((prev) => ({
        ...prev,
        [professional.id]: false,
      }));

      event.target.value = '';
    }
  };


  const handleDeleteProfessionalPhoto = async (index) => {
    const professional = settings.professionals?.[index];

    if (!professional?.id) return;

    const confirmDelete = window.confirm('Remover a foto deste profissional?');
    if (!confirmDelete) return;

    try {
      setUploadingProfessionalPhoto((prev) => ({
        ...prev,
        [professional.id]: true,
      }));

      await api.delete(`/clinic-settings/professionals/${professional.id}/photo`);

      safeToast.success('Foto do profissional removida com sucesso!');
      await fetchSettings();
    } catch (error) {
      console.error('❌ Erro ao remover foto do profissional:', error);
      const msg = safeErrorMessage(error, 'Erro ao remover foto do profissional');
      safeToast.error(msg);
    } finally {
      setUploadingProfessionalPhoto((prev) => ({
        ...prev,
        [professional.id]: false,
      }));
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (uploadingLogo) return;

    if (!file.type.startsWith('image/')) {
      safeToast.error('Apenas arquivos de imagem são permitidos');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      safeToast.error('Arquivo muito grande. Máximo 5MB permitido');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      await api.post('/clinic-settings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      safeToast.success('Logo carregada com sucesso!');
      await fetchSettings();
    } catch (error) {
      console.error('❌ Erro ao fazer upload da logo:', error);
      const msg = safeErrorMessage(error, 'Erro ao fazer upload da logo');
      safeToast.error(msg);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCertUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (uploadingCert) return;

    const fileName = file.name?.toLowerCase?.() || '';
    const allowedExtensions = ['.pfx', '.p12'];
    const isAllowed = allowedExtensions.some((ext) => fileName.endsWith(ext));

    if (!isAllowed) {
      safeToast.error(
        'Apenas arquivos .pfx ou .p12 são permitidos para o certificado A1',
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      safeToast.error(
        'Arquivo de certificado muito grande. Máximo 10MB permitido',
      );
      return;
    }

    setUploadingCert(true);
    try {
      const formData = new FormData();
      formData.append('cert_file', file);
      if (nfseCertPassword) {
        formData.append('password', nfseCertPassword);
      }

      await api.post('/clinic-settings/nfse-cert/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      safeToast.success('Certificado NFS-e enviado com sucesso!');
      setNfseCertPassword('');
      await fetchSettings();
    } catch (error) {
      console.error('❌ Erro ao enviar certificado NFS-e:', error);
      const msg = safeErrorMessage(error, 'Erro ao enviar certificado NFS-e');
      safeToast.error(msg);
    } finally {
      setUploadingCert(false);
    }
  };

  const handleDeleteCert = async () => {
    try {
      await api.delete('/clinic-settings/nfse-cert');
      safeToast.success('Certificado NFS-e removido com sucesso!');
      await fetchSettings();
    } catch (error) {
      console.error('❌ Erro ao remover certificado NFS-e:', error);
      const msg = safeErrorMessage(error, 'Erro ao remover certificado NFS-e');
      safeToast.error(msg);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await api.delete('/clinic-settings/logo');
      safeToast.success('Logo removida com sucesso!');
      await fetchSettings();
    } catch (error) {
      console.error('❌ Erro ao remover logo:', error);
      const msg = safeErrorMessage(error, 'Erro ao remover logo');
      safeToast.error(msg);
    }
  };

  const formatCNPJ = (value) => {
    const numericValue = value.replace(/\D/g, '');
    const part1 = numericValue.slice(0, 2);
    const part2 = numericValue.slice(2, 5);
    const part3 = numericValue.slice(5, 8);
    const part4 = numericValue.slice(8, 12);
    const part5 = numericValue.slice(12, 14);

    let formatted = part1;
    if (part2) formatted += `.${part2}`;
    if (part3) formatted += `.${part3}`;
    if (part4) formatted += `/${part4}`;
    if (part5) formatted += `-${part5}`;

    return formatted;
  };

  const formatCPF = (value) => {
    const numericValue = value.replace(/\D/g, '');
    const part1 = numericValue.slice(0, 3);
    const part2 = numericValue.slice(3, 6);
    const part3 = numericValue.slice(6, 9);
    const part4 = numericValue.slice(9, 11);

    let formatted = part1;
    if (part2) formatted += `.${part2}`;
    if (part3) formatted += `.${part3}`;
    if (part4) formatted += `-${part4}`;

    return formatted;
  };

  const onlyDigits = (value) => String(value || '').replace(/\D/g, '');

  const formatPhoneBR = (value) => {
    const digits = onlyDigits(value).slice(0, 11);

    if (digits.length <= 2) return digits;
    if (digits.length <= 7) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    }

    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (activeTab === 'menu') {
        await updateMenuPreferences({
          hidden_items: hiddenMenuItems,
          preset: menuPreset,
          manually_customized: menuPreset === 'custom',
        });
        safeToast.success('Preferências do menu salvas com sucesso!');
      } else if (activeTab === 'basicos') {
        const payload = { ...settings };
        const response = await api.put('/clinic-settings', payload);

        setSettings((prev) => ({
          ...prev,
          ...response.data,
        }));
        setSavedOnlineBookingToken(response.data?.online_booking_token || '');
        await reloadAuthStatus?.();

        safeToast.success('Configurações salvas com sucesso!');
        if (continueToCompliance) {
          navigate('/conformidade', { replace: true });
        }
      } else {
        const payload = { ...settings };
        const response = await api.put('/clinic-settings', payload);

        setSettings((prev) => ({
          ...prev,
          ...response.data,
        }));

        setSavedOnlineBookingToken(response.data?.online_booking_token || '');

        safeToast.success('Configurações salvas com sucesso!');
      }
    } catch (error) {
      console.error('❌ Erro ao salvar configurações:', error);
      const msg = safeErrorMessage(error, 'Erro ao salvar configurações');
      safeToast.error(msg);
      window.alert(msg);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchSpecialties();
    fetchWhatsappModuleEvents();
  }, []);

  useEffect(() => {
    const handleFocus = async () => {
      await fetchSettings();
      await fetchWhatsappModuleEvents();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    const token = (settings.online_booking_token || '').trim();

    setCopyLinkMessage('');

    if (!token) {
      setOnlineTokenStatus({
        checking: false,
        available: null,
        message: '',
      });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setOnlineTokenStatus({
          checking: true,
          available: null,
          message: 'Verificando identificador...',
        });

        const response = await api.get('/clinic-settings/online-booking-token/check', {
          params: { token },
        });

        setOnlineTokenStatus({
          checking: false,
          available: response.data?.available === true,
          message: response.data?.message || '',
        });
      } catch (error) {
        setOnlineTokenStatus({
          checking: false,
          available: false,
          message: 'Não foi possível verificar o identificador.',
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [settings.online_booking_token]);

  useEffect(() => {
    if (settings.primary_color) {
      document.documentElement.style.setProperty(
        '--clinic-primary',
        settings.primary_color
      );
    }

    if (settings.secondary_color) {
      document.documentElement.style.setProperty(
        '--clinic-secondary',
        settings.secondary_color
      );
    }
  }, [settings.primary_color, settings.secondary_color]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto py-10">
          <p className="text-gray-600">Carregando configurações...</p>
        </div>
      </Layout>
    );
  }

  const mainProfessional =
    settings.professionals?.find((p) => p.is_main) || null;

  const whatsappModuleEnabled =
    settings.whatsapp_module_access_active === true;

  const whatsappModuleKnown =
    ['active', 'cancel_pending', 'expired', 'courtesy', 'pending_payment'].includes(
      settings.whatsapp_module_status
    );

  const whatsappModuleCancelRequested =
    settings.whatsapp_module_status === 'cancel_pending';

  const availableCustomizableMenuItems = CUSTOMIZABLE_MENU_ITEMS
    .filter((item) => item.id !== 'whatsapp_business' || whatsappModuleEnabled);

  const formatUtcDateTimeBR = (value) => {
    if (!value) return '';

    const normalized = String(value).endsWith('Z')
      ? value
      : `${value}Z`;

    return new Date(normalized).toLocaleString('pt-BR', {
      timeZone: 'America/Recife',
    });
  };

  const renderSettingsTab = (tab) => {
    const Icon = tab.icon;
    const isActive = activeTab === tab.id;

    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => setActiveTab(tab.id)}
        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 ${
          isActive
            ? 'border-purple-600 text-purple-700 bg-purple-50'
            : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
        }`}
      >
        <Icon className="w-4 h-4" />
        <span>{tab.name}</span>
      </button>
    );
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6 text-purple-600" />
              Configurações da Clínica
            </h1>
            <p className="text-sm text-gray-500">
              Personalize os dados da sua clínica, documentos, horários e
              visual.
            </p>
          </div>

          {user && (
            <div className="text-right text-xs text-gray-500">
              <p>
                <span className="font-semibold">Usuário:</span> {user.email}
              </p>
              <p>
                <span className="font-semibold">ID:</span> {user.id}
              </p>
            </div>
          )}
        </div>

        <div className="bg-white shadow-sm rounded-xl border border-gray-200">
          <div className="border-b border-gray-200">
            <div className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Configuração inicial
            </div>
            <nav className="-mb-px flex flex-wrap">
              {INITIAL_SETTINGS_TABS.map(renderSettingsTab)}
              <button
                type="button"
                onClick={() =>
                  setShowAdditionalSettings((current) =>
                    ADDITIONAL_SETTINGS_TAB_IDS.has(activeTab) ? true : !current
                  )
                }
                aria-expanded={showAdditionalSettings}
                className={`ml-auto flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 ${
                  showAdditionalSettings || ADDITIONAL_SETTINGS_TAB_IDS.has(activeTab)
                    ? 'border-purple-200 text-purple-700 bg-purple-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span>Mais configurações</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    showAdditionalSettings ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </nav>

            {showAdditionalSettings && (
              <div className="border-t border-gray-100 bg-gray-50">
                <nav className="flex flex-wrap">
                  {ADDITIONAL_SETTINGS_TABS.map(renderSettingsTab)}
                </nav>
              </div>
            )}
          </div>

          <form onSubmit={handleSave}>
            <div className="p-6 space-y-8">
              {activeTab === 'basicos' && (
                <div className="space-y-6">
                  {continueToCompliance && (
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-indigo-900">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-indigo-700 shadow-sm">
                          <ShieldCheck className="h-5 w-5" />
                        </span>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Etapa 1 de 2</p>
                          <h3 className="mt-1 font-semibold">Complete os dados básicos antes de continuar o diagnóstico</h3>
                          <p className="mt-1 text-sm leading-6 text-indigo-800">
                            Depois de salvar, abriremos Boas Práticas automaticamente para você continuar de onde parou e preparar o plano da clínica.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    Dados Básicos
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <Building className="w-4 h-4 inline mr-1" />
                          Nome da Clínica
                        </label>
                        <input
                          type="text"
                          value={settings.clinic_name}
                          onChange={(e) =>
                            handleInputChange('clinic_name', e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                          placeholder="Nome da clínica"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Razão social da clínica
                        </label>
                        <input
                          type="text"
                          value={settings.clinic_legal_name || ''}
                          onChange={(e) =>
                            handleInputChange('clinic_legal_name', e.target.value)
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                          placeholder="Ex.: RS ODONTOLOGIA LTDA"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Usada nos contratos quando a contratada for a clínica.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <User className="w-4 h-4 inline mr-1" />
                            Responsável principal
                          </label>
                          <input
                            type="text"
                            value={settings.professional_name || ''}
                            onChange={(e) =>
                              handleInputChange(
                                'professional_name',
                                e.target.value,
                              )
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                            placeholder="Dr(a). Nome Completo"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <IdCard className="w-4 h-4 inline mr-1" />
                            Número CRO
                          </label>
                          <input
                            type="text"
                            value={settings.cro_number || ''}
                            onChange={(e) =>
                              handleInputChange('cro_number', e.target.value)
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                            placeholder="Ex.: 12345"
                          />
                        </div>
                      </div>

                      <div className="space-y-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1 border-b pb-2">
                          <MapPin className="w-4 h-4 inline mr-1" />
                          Endereço da Clínica
                        </label>
                        <p className="text-xs text-gray-500 mb-2">
                          Este endereço será usado nos documentos, contratos, receituários e no autoagendamento público.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">CEP</label>
                            <input
                              type="text"
                              value={settings.address_cep || ''}
                              onChange={(e) => handleInputChange('address_cep', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>

                          <div className="col-span-1 md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Endereço (Rua, Av.)</label>
                            <input
                              type="text"
                              value={settings.address_street || ''}
                              onChange={(e) => handleInputChange('address_street', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Número</label>
                            <input
                              type="text"
                              value={settings.address_number || ''}
                              onChange={(e) => handleInputChange('address_number', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>

                          <div className="col-span-1 md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Complemento</label>
                            <input
                              type="text"
                              value={settings.address_complement || ''}
                              onChange={(e) => handleInputChange('address_complement', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Bairro</label>
                            <input
                              type="text"
                              value={settings.address_neighborhood || ''}
                              onChange={(e) => handleInputChange('address_neighborhood', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Cidade</label>
                            <input
                              type="text"
                              value={settings.address_city || ''}
                              onChange={(e) => handleInputChange('address_city', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">UF</label>
                            <input
                              type="text"
                              maxLength="2"
                              value={settings.address_state || ''}
                              onChange={(e) => handleInputChange('address_state', e.target.value.toUpperCase())}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 uppercase"
                            />
                          </div>
                        </div>
                      </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              <Phone className="w-4 h-4 inline mr-1" />
                              Telefone da Clínica
                            </label>
                            <input
                              type="text"
                              value={formatPhoneBR(settings.clinic_phone || '')}
                              onChange={(e) =>
                                handleInputChange(
                                  'clinic_phone',
                                  onlyDigits(e.target.value)
                                )
                              }
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                              placeholder="(00) 00000-0000"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              WhatsApp da Clínica
                            </label>
                            <input
                              type="text"
                              value={formatPhoneBR(settings.clinic_whatsapp || '')}
                              onChange={(e) =>
                                handleInputChange(
                                  'clinic_whatsapp',
                                  onlyDigits(e.target.value)
                                )
                              }
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                              placeholder="(00) 00000-0000"
                            />
                          </div>
                        </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <Mail className="w-4 h-4 inline mr-1" />
                            E-mail
                          </label>
                          <input
                            type="email"
                            value={settings.email || ''}
                            onChange={(e) =>
                              handleInputChange('email', e.target.value)
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                            placeholder="contato@clinica.com.br"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            <Globe className="w-4 h-4 inline mr-1" />
                            Website
                          </label>
                          <input
                            type="text"
                            value={settings.website || ''}
                            onChange={(e) =>
                              handleInputChange('website', e.target.value)
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                            placeholder="https://www.clinica.com.br"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duração padrão das consultas (minutos)
                        </label>
                        <input
                          type="number"
                          min="10"
                          max="240"
                          step="5"
                          value={settings.appointment_duration || 30}
                          onChange={(e) =>
                            handleInputChange(
                              'appointment_duration',
                              Number(e.target.value),
                            )
                          }
                          className="w-full md:w-48 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Esse valor é usado como padrão na agenda, mas pode ser
                          ajustado por atendimento.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Logomarca
                        </label>
                        <div className="flex items-center gap-4">
                          <div className="w-24 h-24 border border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50">
                            {settings.logo_data ? (
                              <img
                                src={`data:image/png;base64,${settings.logo_data}`}
                                alt="Logo da clínica"
                                className="max-w-full max-h-full object-contain"
                              />
                            ) : (
                              <span className="text-xs text-gray-400 text-center px-2">
                                Nenhuma logo enviada
                              </span>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="block">
                              <span className="sr-only">Upload de logo</span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoUpload}
                                className="block w-full text-sm text-gray-700"
                              />
                            </label>

                            {settings.logo_filename && (
                              <p className="text-xs text-gray-500">
                                Atual: {settings.logo_filename}
                              </p>
                            )}

                            {settings.logo_data && (
                              <button
                                type="button"
                                onClick={handleDeleteLogo}
                                className="inline-flex items-center px-3 py-1.5 text-xs border border-red-500 text-red-600 rounded-lg hover:bg-red-50"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Remover logo
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Recomenda-se usar uma imagem PNG com fundo
                          transparente para melhor qualidade nos documentos.
                        </p>
                      </div>

                      <div className="border border-purple-100 rounded-lg p-3 bg-purple-50">
                        <p className="text-xs text-purple-800">
                          Os dados acima serão usados na geração de prontuários,
                          receituários, contratos e documentos da clínica.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'profissionais' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Profissionais da Clínica
                  </h3>

                  <p className="text-sm text-gray-600">
                    Configure os profissionais que atendem pela clínica. Um
                    deles será marcado como responsável principal e poderá ser
                    usado como padrão nos documentos.
                  </p>

                  <div className="space-y-4">
                    {settings.professionals.map((prof, index) => (
                      <div
                        key={prof.id || index}
                        className="border border-gray-200 rounded-lg p-4 space-y-3"
                      >
                        <div className="flex flex-col md:flex-row md:items-center gap-4 border-b border-gray-100 pb-4">
                          <div className="flex-shrink-0">
                            {prof.photo_url ? (
                              <img
                                src={prof.photo_url}
                                alt={`Foto de ${prof.name || 'profissional'}`}
                                className="w-24 h-24 rounded-2xl object-cover border border-gray-200 shadow-sm bg-gray-50"
                              />
                            ) : (
                              <div className="w-24 h-24 rounded-2xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                                <User className="w-10 h-10 text-gray-400" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              Foto do profissional
                            </p>
                            <p className="text-xs text-gray-500 mb-3">
                              Esta foto será exibida no autoagendamento quando o paciente escolher este profissional.
                            </p>

                            <div className="flex flex-wrap gap-2">
                              {isMobileOrTablet && (
                                <label className="inline-flex items-center px-3 py-1.5 rounded-md border border-purple-300 text-purple-700 hover:bg-purple-50 text-xs cursor-pointer">
                                  <Camera className="w-4 h-4 mr-1" />
                                  Tirar foto
                                  <input
                                    type="file"
                                    accept="image/*"
                                    capture="user"
                                    className="hidden"
                                    onChange={(e) => handleProfessionalPhotoUpload(index, e)}
                                  />
                                </label>
                              )}

                              <label className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs cursor-pointer">
                                <Upload className="w-4 h-4 mr-1" />
                                {isMobileOrTablet ? 'Escolher foto' : 'Enviar arquivo'}
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  className="hidden"
                                  onChange={(e) => handleProfessionalPhotoUpload(index, e)}
                                />
                              </label>

                              <button
                                type="button"
                                onClick={fetchSettings}
                                disabled={uploadingProfessionalPhoto[prof.id]}
                                className="inline-flex items-center px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs disabled:opacity-50"
                                title="Atualizar foto do profissional"
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Atualizar foto
                              </button>

                              {prof.photo_url && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteProfessionalPhoto(index)}
                                  disabled={uploadingProfessionalPhoto[prof.id]}
                                  className="inline-flex items-center px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 text-xs disabled:opacity-50"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Remover foto
                                </button>
                              )}
                            </div>

                            {uploadingProfessionalPhoto[prof.id] && (
                              <p className="text-xs text-purple-600 mt-2">
                                Enviando foto...
                              </p>
                            )}
                          </div>
                        </div>                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">
                            Profissional {index + 1}
                            {prof.is_main && (
                              <span className="ml-2 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                Responsável principal
                              </span>
                            )}
                          </span>

                          <div className="flex items-center gap-2">
                            {!prof.is_main && (
                              <button
                                type="button"
                                onClick={() => handleSetMainProfessional(index)}
                                className="text-xs text-purple-600 hover:text-purple-800 border border-purple-200 rounded px-2 py-1"
                              >
                                Tornar principal
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => handleRemoveProfessional(index)}
                              className="text-xs text-red-600 hover:text-red-800"
                            >
                              Remover
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Nome completo
                            </label>
                            <input
                              type="text"
                              value={prof.name || ''}
                              onChange={(e) =>
                                handleProfessionalFieldChange(
                                  index,
                                  'name',
                                  e.target.value,
                                )
                              }
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>

                          <div className="grid grid-cols-[2fr_1fr] gap-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                CRO
                              </label>
                              <input
                                type="text"
                                value={prof.cro_number || ''}
                                onChange={(e) =>
                                  handleProfessionalFieldChange(
                                    index,
                                    'cro_number',
                                    e.target.value,
                                  )
                                }
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                UF
                              </label>
                              <input
                                type="text"
                                value={prof.cro_state || ''}
                                onChange={(e) =>
                                  handleProfessionalFieldChange(
                                    index,
                                    'cro_state',
                                    e.target.value,
                                  )
                                }
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                                maxLength={2}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              E-mail
                            </label>
                            <input
                              type="email"
                              value={prof.email || ''}
                              onChange={(e) =>
                                handleProfessionalFieldChange(
                                  index,
                                  'email',
                                  e.target.value,
                                )
                              }
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Telefone
                            </label>
                            <input
                              type="text"
                              value={formatPhoneBR(prof.phone || '')}
                              onChange={(e) =>
                                handleProfessionalFieldChange(
                                  index,
                                  'phone',
                                  onlyDigits(e.target.value),
                                )
                              }
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Especialidade principal do profissional
                          </label>

                          <select
                            value={
                              prof.specialty ||
                              prof.primary_specialty ||
                              prof.specialties?.[0] ||
                              ''
                            }
                            onChange={(e) => {
                              const selectedSpecialty = e.target.value;

                              setSettings((prev) => ({
                                ...prev,
                                professionals: prev.professionals.map((professional, i) =>
                                  i === index
                                    ? {
                                        ...professional,
                                        specialty: selectedSpecialty,
                                        primary_specialty: selectedSpecialty,
                                        specialties: selectedSpecialty ? [selectedSpecialty] : [],
                                      }
                                    : professional,
                                ),
                              }));
                            }}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Selecione uma especialidade</option>
                            {(settings.specialties?.length ? settings.specialties : availableSpecialties).map((specialty) => (
                              <option key={specialty} value={specialty}>
                                {specialty}
                              </option>
                            ))}
                          </select>

                          <p className="text-xs text-gray-500 mt-1">
                            Esta especialidade será exibida nos contratos, termos, carimbos e documentos.
                          </p>
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={handleAddProfessional}
                      className="inline-flex items-center px-3 py-2 text-sm border border-purple-500 text-purple-600 rounded-lg hover:bg-purple-50"
                    >
                      + Adicionar profissional
                    </button>

                    {mainProfessional && (
                      <div className="border border-purple-100 rounded-lg p-3 bg-purple-50 text-xs text-purple-800">
                        <p className="font-semibold text-sm mb-1">
                          Profissional principal:
                        </p>
                        <p>
                          {mainProfessional.name} — CRO{' '}
                          {mainProfessional.cro_number}/{mainProfessional.cro_state}
                        </p>
                        <p className="mt-1">
                          Este profissional será usado como padrão nos
                          documentos quando nenhum outro for selecionado.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'integracoes' && (
                <GoogleCalendarIntegrationSection
                  professionals={settings.professionals || []}
                />
              )}

              {activeTab === 'documentos' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Documentos
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <CreditCard className="w-4 h-4 inline mr-1" />
                        CNPJ
                      </label>
                      <input
                        type="text"
                        value={settings.cnpj}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            cnpj: formatCNPJ(e.target.value),
                          })
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        placeholder="00.000.000/0001-00"
                        maxLength={18}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <IdCard className="w-4 h-4 inline mr-1" />
                        CPF
                      </label>
                      <input
                        type="text"
                        value={settings.cpf}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            cpf: formatCPF(e.target.value),
                          })
                        }
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                    </div>
                  </div>

                  <div className="border border-blue-100 rounded-lg p-3 bg-blue-50 text-xs text-blue-800">
                    <p className="font-semibold text-sm mb-1">
                      Como esses dados serão usados:
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        CNPJ/CPF serão exibidos em receituários, contratos e
                        documentos gerados.
                      </li>
                      <li>
                        Em futuras integrações fiscais (NFS-e), esses dados
                        poderão ser aproveitados como base, junto com os dados
                        específicos configurados na aba de NFS-e.
                      </li>
                    </ul>
                  </div>
                </div>
              )}



              {activeTab === 'especialidades' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Especialidades Oferecidas
                  </h3>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableSpecialties.map((specialty) => (
                      <label
                        key={specialty}
                        className="inline-flex items-center space-x-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={settings.specialties?.includes(specialty)}
                          onChange={() => handleSpecialtyToggle(specialty)}
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                        />
                        <span>{specialty}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'horarios' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Agenda, notificações e autoagendamento
                  </h3>

                  <div
                    className={`rounded-xl border p-4 ${
                      whatsappModuleEnabled
                        ? 'border-green-200 bg-green-50'
                        : 'border-yellow-200 bg-yellow-50'
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <h4
                          className={`font-semibold ${
                            whatsappModuleEnabled ? 'text-green-800' : 'text-yellow-800'
                          }`}
                        >
                          Módulo WhatsApp Business
                        </h4>

                        <p
                          className={`text-sm mt-1 ${
                            whatsappModuleEnabled ? 'text-green-700' : 'text-yellow-700'
                          }`}
                        >
                          {whatsappModuleEnabled
                            ? 'Ativo para esta conta. Você pode enviar confirmações, lembretes, remarcações e cancelamentos por WhatsApp.'
                            : 'Adicional disponível para automatizar confirmações, lembretes, remarcações e cancelamentos por WhatsApp.'}
                        </p>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                          <div className="rounded-lg bg-white/70 border border-white px-3 py-2">
                            <span className="block text-gray-500">Mensagens inclusas</span>
                            <strong className="text-gray-800">
                              {settings.whatsapp_messages_limit || 250}/ciclo
                            </strong>
                          </div>

                          <div className="rounded-lg bg-white/70 border border-white px-3 py-2">
                            <span className="block text-gray-500">Utilizadas neste mês</span>
                            <strong className="text-gray-800">
                              {settings.whatsapp_messages_sent || 0}
                              {" / "}
                              {settings.whatsapp_messages_limit || 250}
                            </strong>
                          </div>

                          <div className="rounded-lg bg-white/70 border border-white px-3 py-2">
                            <span className="block text-gray-500">Excedente</span>
                            <strong className="text-gray-800">
                              {(settings.whatsapp_overage_messages || 0)} msg
                              {' • '}
                              R$ {(settings.whatsapp_overage_amount || 0).toFixed(2).replace('.', ',')}
                            </strong>
                            <span className="block text-gray-500 mt-1">
                              R$ 0,10 por mensagem adicional
                            </span>
                          </div>
                        </div>

                        <p className="text-xs text-gray-600 mt-2">
                          R$ 19,90/mês. Inclui 250 mensagens por ciclo de cobrança. Mensagens adicionais serão calculadas no fechamento do ciclo.
                        </p>

                        {whatsappModuleEvents.length > 0 && (
                          <div className="mt-4 rounded-lg border border-white/70 bg-white/60 p-3">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                              Últimas alterações do módulo
                            </p>

                            <div className="space-y-1">
                              {whatsappModuleEvents.slice(0, 3).map((event, index) => (
                                <div
                                  key={`${event.event_type}-${event.created_at}-${index}`}
                                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-1 text-xs text-gray-600"
                                >
                                  <span>
                                    {event.event_label || event.event_type}

                                    {event.details?.asaas_cancelled === true && (
                                      <span className="ml-1 text-green-700 font-semibold">
                                        · Asaas cancelado
                                      </span>
                                    )}

                                    {event.details?.asaas_cancel_error && (
                                      <span className="ml-1 text-red-700 font-semibold">
                                        · Erro Asaas
                                      </span>
                                    )}
                                  </span>

                                  <span className="text-gray-500">
                                    {formatUtcDateTimeBR(event.created_at)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      </div>

                      {!whatsappModuleKnown ? (
                        <button
                          type="button"
                          onClick={activateWhatsappModulePayment}
                          disabled={whatsappModuleProcessing}
                          className="px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
                        >
                          {whatsappModuleProcessing ? 'Processando...' : 'Contratar módulo'}
                        </button>
                      ) : (
                        <div className="flex flex-col items-start md:items-end gap-2">
                          <span
                            className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${
                              settings.whatsapp_module_status === 'expired'
                                ? 'bg-red-100 text-red-800'
                                : settings.whatsapp_module_status === 'pending_payment'
                                ? 'bg-yellow-100 text-yellow-800'
                                : settings.whatsapp_module_status === 'cancel_pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : settings.whatsapp_module_status === 'courtesy'
                                ? 'bg-purple-100 text-purple-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {settings.whatsapp_module_status_label || 'Ativo'}
                          </span>

                          {whatsappModuleCancelRequested && (
                            <div className="text-xs text-yellow-800 max-w-xs md:text-right">
                              <p>
                                A renovação foi cancelada, mas o módulo permanece ativo até o fim do período já contratado.
                              </p>

                              {settings.whatsapp_module_current_period_end && (
                                <p className="font-semibold mt-1">
                                  Ativo até {formatUtcDateTimeBR(settings.whatsapp_module_current_period_end).split(',')[0]}
                                </p>
                              )}
                            </div>
                          )}

                          {settings.whatsapp_module_status === 'expired' && (
                            <div className="text-xs text-red-700 max-w-xs md:text-right">
                              <p>
                                O período contratado terminou. Para voltar a enviar mensagens por WhatsApp, reative o módulo.
                              </p>
                            </div>
                          )}

                          {settings.whatsapp_module_status === 'pending_payment' && (
                            <div className="text-xs text-yellow-800 max-w-xs md:text-right space-y-2">
                              <p>
                                Já existe uma contratação aguardando pagamento. Continuar reutilizará essa mesma cobrança.
                              </p>
                              <button
                                type="button"
                                onClick={async () => {
                                  await fetchSettings();
                                  await fetchWhatsappModuleEvents();
                                  safeToast.info('Status do módulo atualizado.');
                                }}
                                className="text-yellow-800 underline hover:text-yellow-950"
                              >
                                Atualizar status
                              </button>
                              <div className="flex gap-2 md:justify-end">
                                <button
                                  type="button"
                                  onClick={() => setWhatsappModulePaymentMethod('credit_card')}
                                  className={`px-3 py-1.5 rounded border ${
                                    whatsappModulePaymentMethod === 'credit_card'
                                      ? 'border-yellow-700 bg-yellow-100 font-semibold'
                                      : 'border-yellow-300 bg-white'
                                  }`}
                                >
                                  Cartão
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setWhatsappModulePaymentMethod('boleto')}
                                  className={`px-3 py-1.5 rounded border ${
                                    whatsappModulePaymentMethod === 'boleto'
                                      ? 'border-yellow-700 bg-yellow-100 font-semibold'
                                      : 'border-yellow-300 bg-white'
                                  }`}
                                >
                                  Boleto
                                </button>
                              </div>
                            </div>
                          )}

                          {settings.whatsapp_module_status === 'pending_payment' ? (
                            <button
                              type="button"
                              onClick={activateWhatsappModulePayment}
                              disabled={whatsappModuleProcessing}
                              className="px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 text-sm disabled:opacity-50"
                            >
                              {whatsappModuleProcessing ? 'Processando...' : 'Continuar pagamento'}
                            </button>
                          ) : settings.whatsapp_module_status === 'expired' ? (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await activateWhatsappModulePayment();
                                } catch (error) {
                                  const msg = safeErrorMessage(error, 'Erro ao reativar módulo WhatsApp.');
                                  safeToast.error(msg);
                                }
                              }}
                              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm"
                            >
                              Reativar módulo
                            </button>
                          ) : whatsappModuleCancelRequested ? (
                            <>

                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    const response = await api.post('/clinic-settings/whatsapp-module/reactivate');
                                    setSettings((prev) => ({
                                      ...prev,
                                      whatsapp_module_cancel_at_period_end: false,
                                      ...(response.data || {}),
                                    }));
                                    await fetchSettings();
                                    await fetchWhatsappModuleEvents();
                                    safeToast.success('Cancelamento removido. O módulo continuará ativo.');
                                  } catch (error) {
                                    const msg = safeErrorMessage(error, 'Erro ao reativar módulo WhatsApp.');
                                    safeToast.error(msg);
                                  }
                                }}
                                className="px-4 py-2 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 text-sm"
                              >
                                Manter módulo ativo
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={async () => {
                                if (
                                  window.confirm(
                                    'Deseja cancelar a renovação do Módulo WhatsApp Business? O acesso permanecerá ativo até o fim do período já contratado.'
                                  )
                                ) {
                                  try {
                                    const response = await api.post('/clinic-settings/whatsapp-module/cancel');
                                    setSettings((prev) => ({
                                      ...prev,
                                      whatsapp_module_cancel_at_period_end: true,
                                      ...(response.data || {}),
                                    }));
                                    await fetchSettings();
                                    await fetchWhatsappModuleEvents();
                                    safeToast.success('Cancelamento solicitado.');
                                  } catch (error) {
                                    const msg = safeErrorMessage(error, 'Erro ao cancelar módulo WhatsApp.');
                                    safeToast.error(msg);
                                  }
                                }
                              }}
                              className="px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 text-sm"
                            >
                              Cancelar módulo
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border border-blue-100 rounded-lg p-4 bg-blue-50 space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-blue-900">
                        E-mails de agendamento
                      </h4>
                      <p className="text-xs text-blue-800 mt-1">
                        Configure o envio de confirmação e lembretes automáticos para pacientes com e-mail cadastrado.
                      </p>
                    </div>

                    <div className="flex items-center justify-between gap-4 border border-blue-100 bg-white rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Permitir envio manual de confirmação por e-mail
                        </p>
                        <p className="text-xs text-gray-500">
                          Exibe na Agenda o botão para enviar a confirmação do agendamento ao paciente.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          handleInputChange(
                            'appointment_email_enabled',
                            !settings.appointment_email_enabled
                          )
                        }
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          settings.appointment_email_enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {settings.appointment_email_enabled ? 'Ativo' : 'Inativo'}
                      </button>
                    </div>

                    <div className="flex items-center justify-between gap-4 border border-blue-100 bg-white rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Permitir envio manual de confirmação por WhatsApp
                        </p>
                        <p className="text-xs text-gray-500">
                          Exibe na Agenda o botão para enviar uma confirmação por WhatsApp ao paciente.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (!whatsappModuleEnabled) {
                            safeToast.info('Módulo WhatsApp Business não contratado.');
                            return;
                          }

                          handleInputChange(
                            'appointment_whatsapp_enabled',
                            !settings.appointment_whatsapp_enabled
                          );
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          settings.appointment_whatsapp_enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {settings.appointment_whatsapp_enabled ? 'Ativo' : 'Inativo'}
                      </button>
                    </div>

                    <div className="flex items-center justify-between gap-4 border border-blue-100 bg-white rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Enviar lembrete automático por e-mail
                        </p>
                        <p className="text-xs text-gray-500">
                          O sistema verifica periodicamente os agendamentos futuros e envia um lembrete ao paciente.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          handleInputChange(
                            'appointment_reminder_enabled',
                            !settings.appointment_reminder_enabled
                          )
                        }
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          settings.appointment_reminder_enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {settings.appointment_reminder_enabled ? 'Ativo' : 'Inativo'}
                      </button>
                    </div>

                    <div className="flex items-center justify-between gap-4 border border-blue-100 bg-white rounded-lg p-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          Enviar lembrete automático por WhatsApp
                        </p>
                        <p className="text-xs text-gray-500">
                          O sistema enviará lembretes por WhatsApp para pacientes com telefone cadastrado.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (!whatsappModuleEnabled) {
                            safeToast.info('Módulo WhatsApp Business não contratado.');
                            return;
                          }

                          handleInputChange(
                            'appointment_whatsapp_reminder_enabled',
                            !settings.appointment_whatsapp_reminder_enabled
                          );
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          whatsappModuleEnabled && settings.appointment_whatsapp_reminder_enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {whatsappModuleEnabled && settings.appointment_whatsapp_reminder_enabled
                          ? 'Ativo'
                          : 'Inativo'}
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Antecedência do lembrete
                      </label>
                      <select
                        value={settings.appointment_reminder_days_before || 1}
                        onChange={(e) =>
                          handleInputChange(
                            'appointment_reminder_days_before',
                            Number(e.target.value)
                          )
                        }
                        className="w-full md:w-64 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                      >
                        <option value={1}>1 dia antes</option>
                        <option value={2}>2 dias antes</option>
                        <option value={3}>3 dias antes</option>
                      </select>
                    </div>

                    {!whatsappModuleEnabled && (
                      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                        <p className="font-semibold">
                          Módulo WhatsApp Business não contratado
                        </p>
                        <p className="mt-1">
                          Para usar confirmações, lembretes, remarcações e cancelamentos por WhatsApp,
                          contrate o módulo adicional.
                        </p>
                      </div>
                    )}

                    <div className="border rounded-lg p-4 bg-green-50">
                      <h4 className="font-semibold text-green-800 mb-3">
                        Automações WhatsApp
                      </h4>

                      <div className="space-y-3">

                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            disabled={!whatsappModuleEnabled}
                            checked={settings.appointment_whatsapp_auto_confirmation || false}
                            onChange={(e) =>
                              handleInputChange(
                                'appointment_whatsapp_auto_confirmation',
                                e.target.checked
                              )
                            }
                          />
                          <span>Enviar confirmação automática ao criar agendamento</span>
                        </label>

                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            disabled={!whatsappModuleEnabled}
                            checked={settings.appointment_whatsapp_auto_reschedule || false}
                            onChange={(e) =>
                              handleInputChange(
                                'appointment_whatsapp_auto_reschedule',
                                e.target.checked
                              )
                            }
                          />
                          <span>Enviar WhatsApp automático ao remarcar consulta</span>
                        </label>

                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            disabled={!whatsappModuleEnabled}
                            checked={settings.appointment_whatsapp_auto_cancel || false}
                            onChange={(e) =>
                              handleInputChange(
                                'appointment_whatsapp_auto_cancel',
                                e.target.checked
                              )
                            }
                          />
                          <span>Enviar WhatsApp automático ao cancelar consulta</span>
                        </label>

                      </div>
                    </div>                    
                  </div>

                  <div className="border border-purple-100 rounded-lg p-4 bg-purple-50 space-y-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-semibold text-purple-900">
                          Autoagendamento público
                        </h4>
                        <p className="text-xs text-purple-800 mt-1">
                          Permite que pacientes agendem consultas por um link público da clínica.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          handleInputChange('online_booking_enabled', !settings.online_booking_enabled)
                        }
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          settings.online_booking_enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {settings.online_booking_enabled ? 'Ativo' : 'Inativo'}
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Identificador do link
                      </label>
                      <input
                        type="text"
                        value={settings.online_booking_token || ''}
                        onChange={(e) =>
                          handleInputChange(
                            'online_booking_token',
                            e.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9-_]/g, '')
                          )
                        }
                        placeholder="ex.: minha-clinica"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use apenas letras minúsculas, números, hífen ou underline.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Limite de dias disponíveis
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={settings.online_booking_days_ahead || 30}
                        onChange={(e) =>
                          handleInputChange(
                            'online_booking_days_ahead',
                            Math.min(30, Math.max(1, Number(e.target.value || 30)))
                          )
                        }
                        className="w-full md:w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Por segurança, o autoagendamento fica limitado a no máximo 30 dias.
                      </p>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Antecedência mínima para agendamento (horas)
                        </label>

                        <input
                          type="number"
                          min="0"
                          max="168"
                          value={settings.online_booking_min_notice_hours ?? 2}
                          onChange={(e) =>
                            handleInputChange(
                              'online_booking_min_notice_hours',
                              Math.min(
                                168,
                                Math.max(
                                  0,
                                  Number(e.target.value || 0)
                                )
                              )
                            )
                          }
                          className="w-full md:w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                        />

                        <p className="text-xs text-gray-500 mt-1">
                          Ex.: 2 impede agendamentos com menos de 2 horas de antecedência.
                        </p>
                      </div>

                      <div className="flex items-center justify-between gap-4 border border-purple-100 bg-white rounded-lg p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            Notificar clínica por e-mail
                          </p>

                          <p className="text-xs text-gray-500">
                            Envia um e-mail para a clínica quando houver autoagendamento.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            handleInputChange(
                              'online_booking_notify_clinic',
                              !settings.online_booking_notify_clinic
                            )
                          }
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            settings.online_booking_notify_clinic
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {settings.online_booking_notify_clinic ? 'Ativo' : 'Inativo'}
                        </button>
                      </div>  
                      <div className="flex items-center justify-between gap-4 border border-purple-100 bg-white rounded-lg p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            Exigir confirmação manual da clínica
                          </p>

                          <p className="text-xs text-gray-500">
                            Quando ativo, o agendamento online entra como pendente até a clínica confirmar.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            handleInputChange(
                              'online_booking_requires_manual_confirmation',
                              !settings.online_booking_requires_manual_confirmation
                            )
                          }
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            settings.online_booking_requires_manual_confirmation
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {settings.online_booking_requires_manual_confirmation ? 'Ativo' : 'Inativo'}
                        </button>
                      </div> 
                      <div className="flex items-center justify-between gap-4 border border-purple-100 bg-white rounded-lg p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            Permitir cancelamento pelo paciente
                          </p>
                          <p className="text-xs text-gray-500">
                            Permite que o paciente cancele pelo link recebido no e-mail.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            handleInputChange(
                              'online_booking_allow_patient_cancel',
                              !settings.online_booking_allow_patient_cancel
                            )
                          }
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            settings.online_booking_allow_patient_cancel
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {settings.online_booking_allow_patient_cancel ? 'Ativo' : 'Inativo'}
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Antecedência mínima para cancelamento pelo paciente
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="168"
                          value={settings.online_booking_cancel_min_notice_hours ?? 2}
                          onChange={(e) =>
                            handleInputChange(
                              'online_booking_cancel_min_notice_hours',
                              Math.min(168, Math.max(0, Number(e.target.value || 0)))
                            )
                          }
                          className="w-full md:w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      <div className="flex items-center justify-between gap-4 border border-purple-100 bg-white rounded-lg p-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            Permitir remarcação pelo paciente
                          </p>
                          <p className="text-xs text-gray-500">
                            Permite que o paciente escolha novo horário pelo link recebido no e-mail.
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            handleInputChange(
                              'online_booking_allow_patient_reschedule',
                              !settings.online_booking_allow_patient_reschedule
                            )
                          }
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            settings.online_booking_allow_patient_reschedule
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {settings.online_booking_allow_patient_reschedule ? 'Ativo' : 'Inativo'}
                        </button>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Antecedência mínima para remarcação pelo paciente
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="168"
                          value={settings.online_booking_reschedule_min_notice_hours ?? 2}
                          onChange={(e) =>
                            handleInputChange(
                              'online_booking_reschedule_min_notice_hours',
                              Math.min(168, Math.max(0, Number(e.target.value || 0)))
                            )
                          }
                          className="w-full md:w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500"
                        />
                      </div>                                                               
                    </div>

                    {settings.online_booking_token && (
                      <div className="bg-white border border-purple-100 rounded-lg p-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Link público
                        </label>

                        <div className="flex flex-col md:flex-row gap-2">
                          <input
                            type="text"
                            readOnly
                            value={`${window.location.origin}/public/autoagendamento/${settings.online_booking_token}`}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50"
                          />

                          <button
                            type="button"
                            disabled={
                              !settings.online_booking_enabled ||
                              onlineTokenStatus.checking ||
                              onlineTokenStatus.available === false ||
                              settings.online_booking_token !== savedOnlineBookingToken
                            }
                            onClick={() => {
                              const link = `${window.location.origin}/public/autoagendamento/${settings.online_booking_token}`;
                              navigator.clipboard.writeText(link);
                              setCopyLinkMessage('Link copiado com sucesso.');
                              safeToast.success('Link copiado!');
                            }}
                            className={`px-4 py-2 rounded-lg text-white text-sm ${
                              !settings.online_booking_enabled ||
                              onlineTokenStatus.checking ||
                              onlineTokenStatus.available === false ||
                              settings.online_booking_token !== savedOnlineBookingToken
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-purple-600 hover:bg-purple-700'
                            }`}
                          >
                            Copiar link
                          </button>
                        </div>

                        {onlineTokenStatus.message && (
                          <p className={`text-xs mt-2 ${
                            onlineTokenStatus.available === false ? 'text-red-600' : 'text-green-700'
                          }`}>
                            {onlineTokenStatus.message}
                          </p>
                        )}

                        {settings.online_booking_token !== savedOnlineBookingToken && (
                          <p className="text-xs text-amber-600 mt-2">
                            Salve as configurações antes de copiar este link.
                          </p>
                        )}

                        {copyLinkMessage && (
                          <p className="text-xs text-green-700 mt-2">
                            {copyLinkMessage}
                          </p>
                        )}

                        {!settings.online_booking_enabled && (
                          <p className="text-xs text-red-600 mt-2">
                            O link só funcionará quando o autoagendamento estiver ativo.
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900">
                    Horário de Funcionamento
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Duração padrão das consultas (minutos)
                      </label>
                      <input
                        type="number"
                        min="10"
                        max="240"
                        step="5"
                        value={settings.appointment_duration || 30}
                        onChange={(e) =>
                          handleInputChange(
                            'appointment_duration',
                            Number(e.target.value),
                          )
                        }
                        className="w-full md:w-48 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.keys(settings.working_hours).map((dayKey) => {
                        const day = settings.working_hours[dayKey];
                        return (
                          <div
                            key={dayKey}
                            className={`border rounded-lg p-3 flex items-center justify-between gap-3 ${
                              day.enabled
                                ? 'border-purple-200 bg-purple-50'
                                : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {dayNames[dayKey]}
                              </p>
                              <div className="mt-1 flex items-center gap-2">
                                <input
                                  type="time"
                                  value={day.start}
                                  onChange={(e) =>
                                    handleWorkingHoursChange(
                                      dayKey,
                                      'start',
                                      e.target.value,
                                    )
                                  }
                                  disabled={!day.enabled}
                                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500"
                                />
                                <span className="text-xs text-gray-500">às</span>
                                <input
                                  type="time"
                                  value={day.end}
                                  onChange={(e) =>
                                    handleWorkingHoursChange(
                                      dayKey,
                                      'end',
                                      e.target.value,
                                    )
                                  }
                                  disabled={!day.enabled}
                                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500"
                                />
                              </div>
                              <div className="mt-3 border-t border-purple-100 pt-3 space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-medium text-gray-700">
                                    Intervalo / almoço
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleWorkingHoursChange(
                                        dayKey,
                                        'break_enabled',
                                        !day.break_enabled
                                      )
                                    }
                                    disabled={!day.enabled}
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                                      day.break_enabled
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-200 text-gray-700'
                                    } ${!day.enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  >
                                    {day.break_enabled ? 'Ativo' : 'Inativo'}
                                  </button>
                                </div>

                                {day.break_enabled && (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="time"
                                      value={day.break_start || '12:00'}
                                      onChange={(e) =>
                                        handleWorkingHoursChange(
                                          dayKey,
                                          'break_start',
                                          e.target.value
                                        )
                                      }
                                      disabled={!day.enabled}
                                      className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500"
                                    />

                                    <span className="text-xs text-gray-500">às</span>

                                    <input
                                      type="time"
                                      value={day.break_end || '14:00'}
                                      onChange={(e) =>
                                        handleWorkingHoursChange(
                                          dayKey,
                                          'break_end',
                                          e.target.value
                                        )
                                      }
                                      disabled={!day.enabled}
                                      className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500"
                                    />
                                  </div>
                                )}
                              </div>                              
                            </div>
                            <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() => handleToggleWorkingDay(dayKey)}
                                className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  day.enabled
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-200 text-gray-700'
                                }`}
                              >
                                {day.enabled ? 'Ativo' : 'Fechado'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}



              {activeTab === 'visual' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Visual da Plataforma
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cor primária
                        </label>
                        <input
                          type="color"
                          value={settings.primary_color || '#7c3aed'}
                          onChange={(e) =>
                            handleInputChange('primary_color', e.target.value)
                          }
                          className="w-16 h-10 border border-gray-300 rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Usada em botões principais, destaques e elementos de
                          ação.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cor secundária
                        </label>
                        <input
                          type="color"
                          value={settings.secondary_color || '#f3f4f6'}
                          onChange={(e) =>
                            handleInputChange(
                              'secondary_color',
                              e.target.value,
                            )
                          }
                          className="w-16 h-10 border border-gray-300 rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Usada em fundos de cartões, áreas de listagem e
                          detalhes.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-900 mb-2">
                          Pré-visualização
                        </p>
                        <div className="space-y-3">
                          <div
                            className="h-10 rounded-md flex items-center justify-center text-white text-sm font-medium"
                            style={{
                              backgroundColor:
                                settings.primary_color || '#7c3aed',
                            }}
                          >
                            Botão principal
                          </div>
                          <div
                            className="h-10 rounded-md flex items-center justify-center text-sm font-medium"
                            style={{
                              backgroundColor:
                                settings.secondary_color || '#f3f4f6',
                              color: '#111827',
                            }}
                          >
                            Cartão / Fundo secundário
                          </div>
                        </div>
                      </div>

                      <p className="text-xs text-gray-500">
                        Essas cores afetam apenas o visual da plataforma e não
                        alteram o conteúdo dos documentos gerados. As cores 
                        escolhidas serão utilizadas em toda a identidade visual
                        da plataforma, incluindo botões, destaques, cartões e 
                        demais elementos compatíveis com personalização visual.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'menu' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Personalizar menu
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Escolha quais módulos deseja exibir no menu lateral. Ocultar um módulo não apaga dados nem desativa funcionalidades.
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Esta preferência vale somente para o seu usuário nesta clínica.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900">Nível de detalhes</h4>
                    <p className="mt-1 text-sm text-gray-500">
                      Escolha uma base pronta e ajuste módulos individuais quando quiser.
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {[
                        {
                          key: 'essential',
                          label: 'Essencial',
                          description: 'Dashboard, Agenda, Prontuários, Lembretes e Anamnese.',
                        },
                        {
                          key: 'complete',
                          label: 'Gestão completa',
                          description: 'Exibe todos os módulos disponíveis para seu perfil e sua clínica.',
                        },
                      ].map((preset) => {
                        const active = menuPreset === preset.key;
                        return (
                          <button
                            key={preset.key}
                            type="button"
                            onClick={() => applyMenuPreset(preset.key)}
                            className={`rounded-xl border p-4 text-left transition ${
                              active
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-200'
                            }`}
                          >
                            <p className="font-semibold text-gray-900">{preset.label}</p>
                            <p className="mt-2 text-sm text-gray-600">{preset.description}</p>
                          </button>
                        );
                      })}
                    </div>
                    {menuPreset === 'custom' && (
                      <p className="mt-3 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
                        Personalizado: a seleção abaixo é diferente dos padrões prontos.
                      </p>
                    )}
                  </div>

                  <div className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                    {availableCustomizableMenuItems.map((item) => {
                      const isVisible = !hiddenMenuItems.includes(item.id);
                      return (
                        <div key={item.id} className="flex items-center justify-between gap-4 p-4">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900">{item.name}</p>
                            <p className="text-sm text-gray-500">{item.description}</p>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={isVisible}
                            onClick={() => handleMenuItemToggle(item.id)}
                            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                              isVisible ? 'bg-purple-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                                isVisible ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                            <span className="sr-only">
                              {isVisible ? 'Ocultar' : 'Exibir'} {item.name}
                            </span>
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={() => applyMenuPreset('complete')}
                    className="text-sm font-medium text-purple-700 hover:text-purple-800"
                  >
                    Restaurar gestão completa
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50 rounded-b-xl">
              <p className="text-xs text-gray-500">
                Lembre-se de salvar as alterações antes de sair desta página.
              </p>
              <button
                type="submit"
                disabled={saving}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  saving
                    ? 'bg-purple-300 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving
                  ? 'Salvando...'
                  : activeTab === 'menu'
                    ? 'Salvar preferências'
                    : activeTab === 'basicos' && continueToCompliance
                      ? <><span>Salvar e continuar em Boas Práticas</span><ArrowRight className="ml-2 h-4 w-4" /></>
                      : 'Salvar configurações'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;
